<template>
  <div class="custom-multi-select" v-closable="{ exclude: [], handler: 'onFocusOut' }">
    <b-input-group size="sm" @click="onclick" ref="customInputContainer">
      <template v-slot:append>
        <!-- is_close -->
        <b-input-group-text v-show="!isOpen" class="infoBox infoBoxLast pl-3">
          <i class="fas fa-chevron-down"></i>
        </b-input-group-text>
        <!-- is_open -->
        <b-input-group-text v-show="isOpen" class="infoBox infoBoxLast pl-3">
          <i class="fas fa-chevron-up"></i>
        </b-input-group-text>
      </template>
      <b-input :value="textValue" autocomplete="off" class="custom-select-sm" :disabled="disabled" readonly> </b-input>
    </b-input-group>

    <div v-show="isOpen" class="options-list" :style="'width: ' + calculatedWidth + 'px;'">
      <div class="options-container">
        <div class="options text-left" v-if="!isLoading">
          <ul class="list-unstyled">
            <li v-for="(option, idx) in options" :key="idx" @click="toggle(option)">
              <div class="d-flex">
                <div class="flex-grow-1">
                  <div class="text-truncate" :style="'max-width:' + (calculatedWidth - 120) + 'px;'">
                    <span
                      :class="[
                        highlightOption.id != null && option.id === highlightOption.id ? 'font-weight-bold' : '',
                      ]"
                      >{{ option[textField] }}</span
                    >
                  </div>
                </div>
                <div>
                  <b-badge
                    v-if="highlightOption != null && highlightOption.id != null && option.id === highlightOption.id"
                    :class="['font-weight-normal', option.icon != null && option.id != null ? 'mr-2' : '']"
                    pill
                    >{{ highlightOption.label }}
                  </b-badge>

                  <custom-tooltip
                    v-if="option.icon && option.id"
                    section="benchmarking-select"
                    is-font-awesome-icon
                    :font-awesome-icon="option.icon"
                    :tooltipText="option.tooltip"
                    :index="option.id"
                    :css-class="[option.class, 'pointer', 'small']"
                  ></custom-tooltip>
                </div>
              </div>
            </li>
          </ul>
        </div>

        <div v-else>
          <custom-spinner small :messagge="loadingMessage"></custom-spinner>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CustomSpinner from './CustomSpinner'
import CustomTooltip from './CustomTooltip.vue'
import { convertStringLengthToPixel } from '../../utils/formatter'

let handleOutsideClick = null
export default {
  name: 'CustomSelect',
  components: {
    CustomSpinner,
    CustomTooltip,
  },
  props: {
    selected: Number,
    options: Array,
    textField: String,
    valueField: String,
    disabled: Boolean,
    useTicks: Boolean,
    isLoading: Boolean,
    loadingMessage: String,
    highlightOption: Object,
  },

  data() {
    return {
      isOpen: false,
      text_value: 'None',
      calculatedWidth: 100,
    }
  },

  methods: {
    onclick() {
      if (this.disabled) return
      this.isOpen = !this.isOpen
    },
    onFocusOut: function () {
      this.isOpen = false
      this.$emit('blur')
    },
    toggle(item) {
      if (this.disabled) return
      this.isOpen = false
      this.textValue = item[this.textField]
      item.selected = !item.selected
      this.$emit('input', item)
    },
  },

  computed: {
    textValue: {
      get() {
        return this.text_value
      },
      set(newVal) {
        this.text_value = newVal
      },
    },
    optionsText() {
      return this.options != null && Array.isArray(this.options) && this.options.length > 0
        ? this.options.map((el) => el[this.textField])
        : []
    },
  },

  watch: {
    isOpen: function () {
      const max = 664
      const min = this.$refs.customInputContainer.clientWidth
      const clamp = (num, min, max) => Math.min(Math.max(num, min), max)

      if (this.optionsText == null || this.optionsText.length === 0) this.calculatedWidth = min

      let current_width = convertStringLengthToPixel(this.optionsText)

      this.calculatedWidth = clamp(current_width, min, max)
    },

    selected: function (newVal, oldVal) {
      const option = this.options.filter((el) => el[this.valueField] === newVal)[0]
      if (newVal == null) {
        this.textValue = this.options[0][this.textField]
      }
      if (oldVal == null) {
        /* oldVal == null means it's first time call */
        this.toggle(option)
      }
    },
  },

  created() {
    this.textValue = this.options.filter((el) => el[this.valueField] === this.selected)[0][this.textField]
  },

  directives: {
    closable: {
      bind(el, binding, vnode) {
        handleOutsideClick = (e) => {
          e.stopPropagation()
          const { handler, exclude } = binding.value
          let clickedOnExcludedEl = false
          exclude.forEach((refName) => {
            if (!clickedOnExcludedEl) {
              const excludedEl = vnode.context.$refs[refName]
              clickedOnExcludedEl = excludedEl.contains(e.target)
            }
          })
          if (!el.contains(e.target) && !clickedOnExcludedEl) {
            vnode.context[handler]()
          }
        }
        document.addEventListener('click', handleOutsideClick)
        document.addEventListener('touchstart', handleOutsideClick)
      },

      unbind() {
        document.removeEventListener('click', handleOutsideClick)
        document.removeEventListener('touchstart', handleOutsideClick)
      },
    },
  },
}
</script>
<style scoped lang="scss">
.custom-select-sm {
  border-right: 0;
}

.infoBox {
  background-color: var(--secondaryBGColor);
  border-left: 0;
}

.infoBoxLast {
  border-bottom-right-radius: 0.25rem !important;
  border-top-right-radius: 0.25rem !important;
}

.options-container {
  background-color: var(--secondaryBGColor);
}

.custom-multi-select {
  .custom-select-active {
    color: var(--primary);
    font-weight: 700;
  }

  input {
    &:read-only {
      background-color: #ffffff;
    }
  }

  .options-list {
    position: absolute;
    z-index: 10;
    background-color: #ffffff;
    border: 1px solid #ced4da;
    padding: 0;
    color: #495057;
    font-size: 0.9rem;

    .options {
      height: auto;
      max-height: 300px;
      overflow-y: auto;
      overflow-x: hidden;

      ul {
        margin-bottom: 0;

        li {
          cursor: default;
          padding: 0.25rem 0.75rem;
          white-space: nowrap;

          &:hover {
            background-color: var(--blueVariant);
            color: #000000;
          }
        }
      }
    }
  }
}
</style>
